
import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getMyEvaluations, MyEvaluationItem } from '@/features/People/Evaluations/services/evaluationsService';
import { useNavigate } from 'react-router-dom';
import { ClipboardDocumentCheckIcon, UserIcon, UsersIcon } from '@heroicons/react/24/outline';

export const CollaboratorEvaluationsDashboardPage: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [evaluations, setEvaluations] = useState<MyEvaluationItem[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchEvaluations = async () => {
            if (user?.collaboratorId) {
                try {
                    const data = await getMyEvaluations(user.collaboratorId, user.collaboratorId);
                    setEvaluations(data);
                } catch (error) {
                    console.error("Failed to load evaluations", error);
                } finally {
                    setLoading(false);
                }
            }
        };
        fetchEvaluations();
    }, [user]);

    const selfEvaluations = evaluations.filter(e => e.type === 'SELF');
    const leaderEvaluations = evaluations.filter(e => e.type === 'LEADER');

    const EvaluationCard = ({ item }: { item: MyEvaluationItem }) => {
        const isCompleted = item.status === 'completed';
        const isPending = item.status === 'pending' || item.status === 'draft';

        return (
            <div
                onClick={() => {
                    if (!isCompleted || item.status === 'draft') {
                        const url = `/collaborators/evaluations/${item.campaignId}?view=${item.type}&collaboratorId=${item.targetId}`;
                        navigate(url);
                    }
                }}
                className={`bg-white rounded-lg shadow-sm border border-gray-200 p-5 transition-all cursor-pointer relative overflow-hidden group hover:border-indigo-300 hover:shadow-md
                    ${isCompleted ? 'opacity-80 bg-gray-50' : ''}
                `}
            >
                {/* Status Stripe */}
                <div className={`absolute top-0 left-0 w-1 h-full 
                    ${isCompleted ? 'bg-green-500' : 'bg-indigo-500'}
                `} />

                <div className="flex flex-col h-full justify-between pl-3">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border
                                ${item.type === 'SELF'
                                    ? 'bg-blue-50 text-blue-700 border-blue-100'
                                    : 'bg-purple-50 text-purple-700 border-purple-100'}
                            `}>
                                {item.type === 'SELF' ? 'Autoavaliação' : 'Avaliação de Liderado'}
                            </span>
                            {item.status === 'draft' && (
                                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100">
                                    Rascunho
                                </span>
                            )}
                        </div>
                        <h3 className="text-base font-bold text-gray-900 leading-tight">{item.campaignTitle}</h3>
                        <p className="text-sm text-gray-500 mt-1">
                            Referente a: <span className="font-medium text-gray-700">{item.targetName}</span>
                        </p>
                    </div>

                    <div className="mt-4 flex items-center justify-between">
                        {item.deadline && (
                            <p className="text-xs text-gray-400 flex items-center gap-1">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                                {item.deadline.toLocaleDateString()}
                            </p>
                        )}

                        {isCompleted ? (
                            <div className="flex items-center gap-1 text-green-600 font-medium text-xs bg-green-50 px-2 py-1 rounded-md border border-green-100">
                                <ClipboardDocumentCheckIcon className="w-4 h-4" />
                                <span>Concluída</span>
                            </div>
                        ) : (
                            <button className="px-3 py-1.5 bg-indigo-50 text-indigo-700 text-xs font-semibold rounded-md border border-indigo-100 hover:bg-indigo-100 transition-colors">
                                {item.status === 'draft' ? 'Continuar' : 'Iniciar'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="p-8 flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <header className="flex flex-col gap-1">
                <h1 className="text-2xl font-bold text-gray-900">Minhas Avaliações</h1>
                <p className="text-gray-500">Acompanhe suas autoavaliações e avaliações pendentes.</p>
            </header>

            <div className="space-y-8">
                {/* Self Evaluations Section */}
                <section>
                    <div className="flex items-center gap-2 mb-4">
                        <div className="p-1.5 bg-indigo-100 rounded-lg">
                            <UserIcon className="w-5 h-5 text-indigo-700" />
                        </div>
                        <h2 className="text-lg font-semibold text-gray-900">Minhas Autoavaliações</h2>
                    </div>

                    {selfEvaluations.length === 0 ? (
                        <div className="text-center py-10 bg-white rounded-lg border border-gray-200 shadow-sm">
                            <p className="text-gray-500 text-sm">Nenhuma autoavaliação pendente ou disponível.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {selfEvaluations.map((item, idx) => (
                                <EvaluationCard key={`${item.campaignId}-self-${idx}`} item={item} />
                            ))}
                        </div>
                    )}
                </section>

                {/* Leader Evaluations Section */}
                {leaderEvaluations.length > 0 && (
                    <section>
                        <div className="flex items-center gap-2 mb-4">
                            <div className="p-1.5 bg-purple-100 rounded-lg">
                                <UsersIcon className="w-5 h-5 text-purple-700" />
                            </div>
                            <h2 className="text-lg font-semibold text-gray-900">Avaliações para Fazer (Líder)</h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {leaderEvaluations.map((item, idx) => (
                                <EvaluationCard key={`${item.campaignId}-leader-${idx}`} item={item} />
                            ))}
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
};
